/**
 * Waiting cat queue — patience, bored behaviors, spawn refill.
 *
 * See docs/PLAN.md §4.3 (Spawn / Queue / Placement).
 */

import type { RunConfig } from './run-config.js';
import { pickSpawnArchetype } from './spawn-weighting.js';
import type { BoredBehavior, BoredBehaviorWeights, CatArchetype } from './types.js';

export interface WaitingCat {
  queueId: string;
  archetypeId: string;
  slotIndex: number;
  /** 0..1 — rises while waiting; triggers bored behavior at 1. */
  patience: number;
  behavior: BoredBehavior | null;
  /** walk_away uses telegraph before leaving. */
  behaviorPhase: 'none' | 'telegraph' | 'active';
  behaviorSeconds: number;
}

export interface QueueTickResult {
  personRestlessnessRate: number;
  /** Per-second restlessness added to bottom stack cats during yowl. */
  yowlStackRestlessnessRate: number;
  /** Trigger floor-toy escalation (bottom of stack). */
  triggerFloorToy: boolean;
  /** Queue ids removed this tick (walk away). */
  removedQueueIds: string[];
}

let nextQueueId = 0;

export function createQueueId(): string {
  return `wait_${nextQueueId++}`;
}

export function rollBoredBehavior(
  weights: BoredBehaviorWeights,
  rng: () => number = Math.random,
): BoredBehavior {
  const entries: [BoredBehavior, number][] = [
    ['walk_away', weights.walk_away],
    ['bat_sleeper', weights.bat_sleeper],
    ['yowl', weights.yowl],
    ['play_toy', weights.play_toy],
    ['knead_pillow', weights.knead_pillow],
  ];
  const total = entries.reduce((s, [, w]) => s + w, 0);
  if (total <= 0) return 'knead_pillow';
  let roll = rng() * total;
  for (const [behavior, weight] of entries) {
    roll -= weight;
    if (roll <= 0) return behavior;
  }
  return entries[entries.length - 1]?.[0] ?? 'knead_pillow';
}

export function createWaitingCat(archetypeId: string, slotIndex: number): WaitingCat {
  return {
    queueId: createQueueId(),
    archetypeId,
    slotIndex,
    patience: 0,
    behavior: null,
    behaviorPhase: 'none',
    behaviorSeconds: 0,
  };
}

/** Advance patience and execute bored behaviors for all waiting cats. */
export function tickWaitingCats(
  waiting: WaitingCat[],
  archetypes: ReadonlyMap<string, CatArchetype>,
  config: RunConfig,
  dtSeconds: number,
  heldQueueId: string | null,
): QueueTickResult {
  const result: QueueTickResult = {
    personRestlessnessRate: 0,
    yowlStackRestlessnessRate: 0,
    triggerFloorToy: false,
    removedQueueIds: [],
  };

  for (const cat of waiting) {
    if (cat.queueId === heldQueueId) continue;

    if (!cat.behavior) {
      cat.patience = Math.min(1, cat.patience + config.queuePatiencePerSecond * dtSeconds);
      if (cat.patience >= 1) {
        const archetype = archetypes.get(cat.archetypeId);
        cat.behavior = rollBoredBehavior(archetype?.boredWeights ?? defaultWeights());
        cat.behaviorPhase = cat.behavior === 'walk_away' ? 'telegraph' : 'active';
        cat.behaviorSeconds = 0;
        if (cat.behavior === 'play_toy') result.triggerFloorToy = true;
      }
      continue;
    }

    cat.behaviorSeconds += dtSeconds;

    switch (cat.behavior) {
      case 'walk_away':
        if (cat.behaviorPhase === 'telegraph') {
          if (cat.behaviorSeconds >= config.boredWalkTelegraphSeconds) {
            result.removedQueueIds.push(cat.queueId);
          }
        }
        break;
      case 'bat_sleeper':
        result.personRestlessnessRate += config.boredBatPersonRatePerSecond;
        if (cat.behaviorSeconds >= config.boredBatDurationSeconds) {
          resetAfterBehavior(cat, config);
        }
        break;
      case 'yowl':
        result.personRestlessnessRate += config.boredYowlPersonRatePerSecond;
        result.yowlStackRestlessnessRate += config.boredYowlStackRatePerSecond;
        if (cat.behaviorSeconds >= config.boredYowlDurationSeconds) {
          resetAfterBehavior(cat, config);
        }
        break;
      case 'play_toy':
        if (cat.behaviorSeconds >= config.boredPlayToyDurationSeconds) {
          resetAfterBehavior(cat, config);
        }
        break;
      case 'knead_pillow':
        if (cat.behaviorSeconds >= config.boredKneadDurationSeconds) {
          resetAfterBehavior(cat, config);
        }
        break;
    }
  }

  return result;
}

function resetAfterBehavior(cat: WaitingCat, config: RunConfig): void {
  cat.behavior = null;
  cat.behaviorPhase = 'none';
  cat.behaviorSeconds = 0;
  cat.patience = config.boredPatienceReset;
}

function defaultWeights(): BoredBehaviorWeights {
  return {
    walk_away: 1,
    bat_sleeper: 1,
    yowl: 1,
    play_toy: 1,
    knead_pillow: 1,
  };
}

/** Refill empty slots up to queueVisibleCount. */
export function refillQueue(
  waiting: WaitingCat[],
  archetypes: readonly CatArchetype[],
  config: RunConfig,
  stackHeightAboveBed: number,
  placedCount: number,
  lastSpawnedId: string | null,
): string | null {
  let lastId = lastSpawnedId;
  const usedSlots = new Set(waiting.map((c) => c.slotIndex));
  for (let slot = 0; slot < config.queueVisibleCount; slot++) {
    if (usedSlots.has(slot)) continue;
    if (waiting.length >= config.queueVisibleCount) break;
    const picked = pickSpawnArchetype(archetypes, config, stackHeightAboveBed, placedCount, lastId);
    waiting.push(createWaitingCat(picked.id, slot));
    lastId = picked.id;
  }
  return lastId;
}

/** Remove waiting cats by id and compact. */
export function removeWaitingCats(waiting: WaitingCat[], ids: readonly string[]): void {
  const drop = new Set(ids);
  for (let i = waiting.length - 1; i >= 0; i--) {
    if (drop.has(waiting[i]?.queueId ?? '')) waiting.splice(i, 1);
  }
}

/** Hit test a waiting cat at world coordinates (slot circles). */
export function hitTestWaitingCat(
  waiting: readonly WaitingCat[],
  slotPositions: readonly { x: number; y: number }[],
  wx: number,
  wy: number,
  hitRadius: number,
): WaitingCat | null {
  for (const cat of waiting) {
    const slot = slotPositions[cat.slotIndex];
    if (!slot) continue;
    const dx = wx - slot.x;
    const dy = wy - slot.y;
    if (dx * dx + dy * dy <= hitRadius * hitRadius) return cat;
  }
  return null;
}
